import dayjs from 'dayjs';
import { isDecreasing, isMeasurementValue, isMultiMeasure, measureOfUnit, readMeasurement, sortGroupOf } from './units';
import { readCurrency } from './currency';
import { formatLocation } from './elementFormat/location';
import { parseStoredTime } from './dateDisplay';
import { earliestDateValue, MULTI_DATE_TYPES } from './dateOptions';
import { readCount } from './confirmationValues';
import { countEntries } from './repeatingGroupEntries';

const DATE_LIST_TYPES = new Set(MULTI_DATE_TYPES);

// FALSE is a value, not a blank: a yes/no element answered "No" sorts with
// the answered items, never with the unset ones.
export const isEmptyValue = val => val === undefined || val === null || val === '';

export const parseNumber = val => {
	const num = Number(val);
	return Number.isNaN(num) ? null : num;
};

export const parseDateValue = val => {
	const parsed = dayjs(val);
	return parsed.isValid() ? parsed.valueOf() : null;
};

/** A stored `time` ('HH:mm', or a legacy ISO timestamp) as minutes past midnight. */
const minutesOfDay = val => {
	const parsed = parseStoredTime(val);
	return parsed ? parsed.hour() * 60 + parsed.minute() : null;
};

export const getFieldValue = (item, field) => {
	if (!item) return undefined;
	if (field === 'createdAt') {
		return item.createdAt ?? item.created_at ?? item.createdOn ?? item.created_on;
	}
	if (field === 'updatedAt') {
		return item.updatedAt ?? item.updated_at ?? item.lastUpdate ?? item.last_update;
	}
	return item.elements?.[field];
};

/**
 * A measurement sorts on its canonical base, the way the API sorts
 * (`elements.<name>.base`). A legacy bare number under a measurement
 * element has no base and sorts with the empties — the documented server
 * behaviour, mirrored so the client's re-sort never disagrees with the
 * order the list arrived in. An element whose default unit is reciprocal (a
 * fuel economy in L/100km) sorts by the number it shows, as the API does
 * (`baseRunsBackwards`): its key is the base negated.
 */
const measurementSortKey = (raw, options) => {
	if (!isMeasurementValue(raw)) return null;
	const read = readMeasurement(raw, options || {});
	if (!read || !Number.isFinite(read.base)) return null;
	// An element that allows several measures (KPR-1085) sorts as the API
	// sorts it: grouped by measure (A to Z), then by base — no reciprocal turn.
	// Its own units (KPR-1109) group after every measure, one group per unit:
	// the vendored sortGroupOf is the API's one rule.
	if (isMultiMeasure(options || {})) return { measure: sortGroupOf(measureOfUnit(read.unit)), base: read.base };
	return isDecreasing((options || {}).defaultUnit) ? -read.base : read.base;
};

/**
 * Money sorts as the API sorts it (docs/plans/currency.md "Query and sort"):
 * by currency code, then by amount — a mixed column groups CAD before USD,
 * a one-currency column is simply by amount. A legacy bare number reads as
 * major units in the element's default currency (readCurrency's tolerance);
 * anything unreadable lands with the empties.
 */
const currencySortKey = (raw, options) => readCurrency(raw, options || {});

/**
 * Type-aware comparator for item records. Empty values always sort last
 * regardless of direction. `options` is the element's options — only a
 * measurement needs them (its measure decides what the base means).
 * `getValue(item)`, when given, replaces the field read — the primary-date
 * sort reads a different field per item, through its card.
 */
const sortKey = (item, field, dataType, options, getValue) => {
	const raw = typeof getValue === 'function' ? getValue(item) : getFieldValue(item, field);
	// A date element may hold SEVERAL dates (utils/dateOptions
	// `allowMultiple`). It sorts on the EARLIEST — the first dose, the first
	// visit, the day the thing began — which is the same value the primary
	// date, the day groups and the tile header read, so a list never sorts one
	// way and reads another. An empty list is empty, and lands with the blanks.
	if (Array.isArray(raw) && DATE_LIST_TYPES.has(dataType)) {
		return earliestDateValue(dataType, raw) ?? null;
	}
	// A Choice that allows multiple values sorts on its FIRST entry — the
	// first in the choices' order, which is how it is stored — the key the
	// API sorts on (keepr-api utils/queryContract.js). An empty list is empty.
	if (Array.isArray(raw) && dataType === 'choice') return raw.length ? raw[0] : null;
	if (dataType === 'measurement') return measurementSortKey(raw, options);
	if (dataType === 'currency') return currencySortKey(raw, options);
	// A location sorts on the text it SHOWS — its address, or its
	// coordinates when it has no address — which is what the server sorts on
	// (`elements.<name>.address`) and what the reader sees in the column. An
	// empty location ('' or {}) formats to '' and lands with the empties.
	if (dataType === 'location') return formatLocation(raw);
	// A "Me too" count (KPR-390): absent reads as 0, so an item nobody
	// confirmed sorts beside the zeros — never with the blanks at the end —
	// as the API sorts `elements.<name>` (absent beside 0).
	if (dataType === 'confirmations') return readCount(raw);
	// A repeating group sorts by how many entries it holds, a heading not one
	// (KPR-683; the API sorts by the same number): none is 0, not blank.
	if (dataType === 'repeating-group') return countEntries(raw).real;
	return raw;
};

/** Two non-empty keys of one data type, ascending: negative when `aVal` sorts first. */
const compareKeys = (aVal, bVal, dataType) => {
	switch (dataType) {
		case 'number':
		case 'decimal':
		case 'integer':
		case 'rating':
		case 'confirmations':
		case 'repeating-group':
		case 'measurement': {
			const left = parseNumber(aVal) ?? Number.NEGATIVE_INFINITY;
			const right = parseNumber(bVal) ?? Number.NEGATIVE_INFINITY;
			return left - right;
		}
		case 'currency':
			return aVal.amount - bVal.amount;
		// No before Yes, the way the server orders a BSON boolean.
		case 'boolean':
			return (aVal === true ? 1 : 0) - (bVal === true ? 1 : 0);
		case 'date':
		case 'date-time': {
			const left = parseDateValue(aVal) ?? Number.NEGATIVE_INFINITY;
			const right = parseDateValue(bVal) ?? Number.NEGATIVE_INFINITY;
			return left - right;
		}
		// 'HH:mm' sorts lexicographically all on its own, but a list may hold
		// both shapes while legacy ISO values are still around, so both go
		// through the stored-time reader and compare as minutes of the day.
		case 'time': {
			const left = minutesOfDay(aVal) ?? Number.NEGATIVE_INFINITY;
			const right = minutesOfDay(bVal) ?? Number.NEGATIVE_INFINITY;
			return left - right;
		}
		default:
			return (aVal + '').localeCompare(bVal + '', undefined, { sensitivity: 'base' });
	}
};

/**
 * `direction` ('asc', the default, or 'desc') is applied HERE, inside the
 * comparator, and nowhere else: empty values are last in both directions (as
 * the API returns them), a descending sort negates the order of the non-empty
 * values, and a currency column keeps its codes ascending whichever way the
 * amounts run. So the caller sorts once and never reverses the list: a
 * reverse would put the blanks first and flip the order of equal values, which
 * the stable sort leaves exactly as they arrived (the server's order).
 */
export const compareRecords = (a, b, field, dataType, options, getValue, direction = 'asc') => {
	const aVal = sortKey(a, field, dataType, options, getValue);
	const bVal = sortKey(b, field, dataType, options, getValue);
	const aEmpty = isEmptyValue(aVal);
	const bEmpty = isEmptyValue(bVal);

	if (aEmpty && bEmpty) return 0;
	if (aEmpty) return 1;
	if (bEmpty) return -1;

	if (dataType === 'currency' && aVal.currency !== bVal.currency) {
		return aVal.currency < bVal.currency ? -1 : 1;
	}
	// Several measures: the measures ascending whichever way the bases run.
	if (dataType === 'measurement' && aVal && typeof aVal === 'object' && bVal && typeof bVal === 'object') {
		if (aVal.measure !== bVal.measure) return aVal.measure < bVal.measure ? -1 : 1;
		return ((direction === 'desc' ? -1 : 1) * (aVal.base - bVal.base)) || 0;
	}
	// `|| 0` keeps an equal pair at +0 (never -0) and an unorderable one at 0.
	return ((direction === 'desc' ? -1 : 1) * compareKeys(aVal, bVal, dataType)) || 0;
};

/**
 * Alphabetical order for the records that carry their `name` at the top level:
 * the cards, the collections and the sub-collections of a pick list or a
 * Home. An item keeps its values under `elements` and has no `name` of its
 * own, so `compareRecords(a, b, 'name')` reads the wrong place for these and
 * leaves every list in the order it arrived (KPR-895). Ascending,
 * case-insensitive, and a record with no name (missing, null, empty or only
 * spaces) is last; equal names compare equal, so the stable sort keeps them
 * as they arrived.
 */
const ownName = record => {
	const name = record?.name;
	return typeof name === 'string' ? name.trim() : name;
};

export const compareByName = (a, b) => compareRecords(a, b, 'name', undefined, undefined, ownName);
